import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Download, Search, X, CreditCard, Receipt, User, Calendar as CalendarDays, DollarSign, MoreVertical, Shield, Eye, FileText, Settings, MessageCircleQuestion, Info, ScanQrCode, ChevronUp, ChevronDown } from 'lucide-react';
import { saleService } from '@/services/sale.service';
import { clientService } from '@/services/client.service';
import { toast } from 'sonner';
import { GarantiaModal } from '@/components/GarantiaModal';
import { ReceiptModal } from '@/components/ReceiptModal';
import { WarrantyModal } from '@/components/WarrantyModal';
import { telegramLogService } from '@/services/telegram-log.service';

export interface Sale {
  id: number;
  codepagarme?: string;
  amount: number;
  nome_cliente?: string;
  payment_method: string;
  date_created: string;
  situation?: string;
  installments: number;
  warranty_id: number;
  userClient: {
    id: number;
    codigo: string;
  };
  warranty?: {
    idwarranty: number;
    url_download?: string;
    data_compra: string;
    client: {
      nome_cliente: string;
      telefone_cliente: string;
      cpf_cliente: string;
    };
    codigos: string;
  };
}

export interface CodigoPeca {
  id: number;
  code: string;
  edit?: boolean;
}

export interface GraphData {
  mes: string;
  total_vendido: number;
}

export default function MinhasVendas() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [filteredSales, setFilteredSales] = useState<Sale[]>([]);
  const [totalSalesMonth, setTotalSalesMonth] = useState<number>(0);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userData, setUserData] = useState<any>(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState<boolean>(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
  const [warrantyModalOpen, setWarrantyModalOpen] = useState<boolean>(false);
  
  // Estados para modal de garantia
  const [garantiaModalOpen, setGarantiaModalOpen] = useState<boolean>(false);
  const [saleForGarantia, setSaleForGarantia] = useState<Sale | null>(null);
  const [garantiaFormData, setGarantiaFormData] = useState({
    cpf_cliente: '',
    telefone_cliente: '',
    nome_cliente: '',
    codigos: [] as CodigoPeca[],
    idcliente: null as number | null,
    sale: {} as any,
    iduser: null as number | null,
    sale_date_created: ''
  });
  const [foundClient, setFoundClient] = useState<boolean>(false);
  const [isSubmittingGarantia, setIsSubmittingGarantia] = useState<boolean>(false);
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('userlogged');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserData(user);
      loadData(user);
    }
  }, []);

  // Effect para controlar a visibilidade do botão "voltar ao topo"
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowScrollToTop(scrollPosition > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadData = async (user: any) => {
    setIsLoading(true);
    try {
      await Promise.all([
        getAllSalesByUserId(user.id),
        getSalesMonthUser(user.id)
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados das vendas');
    } finally {
      setIsLoading(false);
    }
  };

  const getAllSalesByUserId = async (userId: number) => {
    try {
      const response = await saleService.getAllSalesByUserId(userId);
      if (response && Array.isArray(response)) {
        setSales(response);
        setFilteredSales(response);
      }
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
      toast.error('Erro ao buscar vendas');
    }
  };

  const getSalesMonthUser = async (userId: number) => {
    try {
      const response = await saleService.getTotalSalesThisMonthUser(userId);
      if (response && response.length > 0) {
        const totalValue = Number(Number(response[0].total_vendido).toFixed(2));
        // Animated counter effect
        animateValue(0, totalValue, 2000);
      }
    } catch (error) {
      console.error('Erro ao buscar vendas do mês:', error);
      toast.error('Erro ao buscar vendas do mês');
    }
  };

  const animateValue = (start: number, end: number, duration: number) => {
    const range = end - start;
    const stepTime = 20;
    const steps = duration / stepTime;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setTotalSalesMonth(start + (range * currentStep / steps));
      if (currentStep >= steps) {
        setTotalSalesMonth(end);
        clearInterval(timer);
      }
    }, stepTime);
  };

  const applyFilter = (value: string) => {
    setSearchFilter(value);
    filterSales(value, startDate, endDate);
  };

  const filterSales = (search: string, start?: Date, end?: Date) => {
    let filtered = [...sales];

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(sale =>
        (sale.codepagarme && sale.codepagarme.toLowerCase().includes(searchLower)) ||
        (sale.nome_cliente && sale.nome_cliente.toLowerCase().includes(searchLower)) ||
        mapPaymentMethod(sale.payment_method).toLowerCase().includes(searchLower)
      );
    }

    // Apply date range filter
    if (start && end) {
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.date_created);
        return saleDate >= start && saleDate <= end;
      });
    }

    setFilteredSales(filtered);
  };

  const aplicarFiltroPorPeriodo = () => {
    if (startDate && endDate) {
      filterSales(searchFilter, startDate, endDate);
    }
  };

  const limparDatas = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    filterSales(searchFilter);
  };

  const mapPaymentMethod = (paymentMethod: string): string => {
    switch (paymentMethod) {
      case 'credit_card':
        return 'Cartão Créd.';
      case 'pix':
        return 'PIX';
      default:
        return 'Não identificado';
    }
  };

  const getSituationStyles = (situation?: string) => {
    switch (situation?.toLowerCase()) {
      case 'pendente':
        return {
          card: 'border-blue-200 bg-blue-50',
          badge: 'bg-blue-100 text-blue-800 border-blue-200'
        };
      case 'aprovado':
        return {
          card: 'border-blue-400 bg-blue-100',
          badge: 'bg-blue-100 text-green-800 border-blue-400'
        };
      case 'baixado':
        return {
          card: 'border-green-200 bg-green-50',
          badge: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'reprovado':
        return {
          card: 'border-yellow-200 bg-yellow-50',
          badge: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      default:
        return {
          card: 'border-gray-200 bg-gray-50',
          badge: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };

  const getSituationText = (situation?: string): string => {
    switch (situation?.toLowerCase()) {
      case 'pendente':
        return 'Em Aberto';
      case 'aprovado':
        return 'Aprovado';
      case 'baixado':
        return 'Baixado';
      case 'reprovado':
        return 'Reprovado';
      default:
        return 'Não informado';
    }
  };

  const formatCurrency = (amount: number): string => {
    return (amount / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatDate = (dateString: string): string => {
    // format 10 de out. de 2025
    return format(new Date(dateString), "dd 'de' MMM. 'de' yyyy, HH:mm", { locale: ptBR });
  };

  const downloadReceipt = async () => {
    if (!selectedSale) return;
    
    const element = document.getElementById('receipt-content');
    if (!element) return;

    try {
      // Usando html2canvas para capturar o elemento
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true
      });
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `Recibo_Compra_${selectedSale.codepagarme || `XDINF${selectedSale.id}`}.png`;
      link.click();
      
      toast.success('Recibo baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar recibo:', error);
      toast.error('Erro ao gerar recibo');
    }
  };

  const downloadWarranty = async () => {
    if (!selectedSale) return;
    
    const element = document.getElementById('warranty-content');
    if (!element) return;

    try {
      // Usando html2canvas para capturar o elemento
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        background: '#e9e9e9'
      });
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `Garantia_${selectedSale.codepagarme || `XDINF${selectedSale.id}`}.png`;
      link.click();
      
      toast.success('Certificado de garantia baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar certificado:', error);
      toast.error('Erro ao gerar certificado de garantia');
    }
  };

  const exportPdf = () => {
    const titleDownload = `Exportacao_Minhas_Vendas_${userData?.nome_razao_social?.replace(' ', '_') || 'Usuario'}`;

    // Create CSV content (simplified export for web)
    const headers = ['Código', 'Mét. Pagamento', 'Valor', 'Cliente', 'Data', 'Situação'];
    const csvContent = [
      headers.join(','),
      ...filteredSales.map(sale => [
        sale.codepagarme || `XDINF${sale.id}`,
        mapPaymentMethod(sale.payment_method),
        formatCurrency(sale.amount),
        sale.nome_cliente || 'Não informado',
        formatDate(sale.date_created),
        getSituationText(sale.situation)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${titleDownload}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Arquivo CSV exportado com sucesso!');
  };

  const exportExcel = () => {
    exportPdf(); // Using CSV for simplicity in web environment
  };

  // Funções para modal de garantia
  const openGarantiaModal = (sale: Sale) => {
    setSaleForGarantia(sale);
    setGarantiaFormData({
      cpf_cliente: '',
      telefone_cliente: '',
      nome_cliente: sale.nome_cliente || '',
      codigos: [] as CodigoPeca[],
      idcliente: null,
      sale: sale,
      iduser: userData?.id || null,
      sale_date_created: ''
    });
    setFoundClient(false);
    setGarantiaModalOpen(true);
  };

  const closeGarantiaModal = () => {
    setGarantiaModalOpen(false);
    setSaleForGarantia(null);
    setGarantiaFormData({
      cpf_cliente: '',
      telefone_cliente: '',
      nome_cliente: '',
      codigos: [] as CodigoPeca[],
      idcliente: null,
      sale: {},
      iduser: null,
      sale_date_created: ''
    });
    setFoundClient(false);
    setIsSubmittingGarantia(false);
  };

  const validateCpfCnpj = (value: string): boolean => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length === 11) {
      // Validação básica de CPF
      return cleanValue.length === 11;
    } else if (cleanValue.length === 14) {
      // Validação básica de CNPJ
      return cleanValue.length === 14;
    }
    return false;
  };

  const formatCpfCnpj = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 11) {
      // Formato CPF
      return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      // Formato CNPJ
      return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const formatPhone = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 10) {
      return cleanValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const handleCpfChange = (value: string) => {
    const formatted = formatCpfCnpj(value);
    setGarantiaFormData(prev => ({ ...prev, cpf_cliente: formatted }));
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    setGarantiaFormData(prev => ({ ...prev, telefone_cliente: formatted }));
  };

  const handleNameChange = (value: string) => {
    // Capitalizar primeira letra de cada palavra
    const formatted = value.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
    setGarantiaFormData(prev => ({ ...prev, nome_cliente: formatted }));
  };

  const searchClient = async (dado: string, valor: string) => {
    try {
      const response = await clientService.getClient(dado, valor);
      
      if (!response.id) {
        toast.info(`Cliente não encontrado, verifique o ${valor === 'telefone_cliente' ? 'telefone' : 'CPF'} ou continue inserindo para criar um novo cliente.`);
        return;
      }

      toast.success('Cliente encontrado! Prossiga com os códigos.');
      setGarantiaFormData(prev => ({
        ...prev,
        idcliente: response.id,
        nome_cliente: response.nome_cliente,
        cpf_cliente: response.cpf_cliente,
        telefone_cliente: response.telefone_cliente.replace("55", "").trim()
      }));
      setFoundClient(true);
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      toast.error('Erro ao buscar cliente');
    }
  };

  const addCodigo = (codigo: string, tipo: string) => {
    // Remove todos os caracteres não numéricos
    const cleanCodigo = codigo.replace(/\D/g, '');
    
    // Validação de 13 dígitos
    if (cleanCodigo.length !== 13) {
      toast.error('Cód inválido: fora do padrão de 13 dígitos');
      return;
    }

    // Verifica se já existe
    const codigoCompleto = `${cleanCodigo} (${tipo})`;
    const exists = garantiaFormData.codigos.some(c => c.code === codigoCompleto);
    
    if (exists) {
      toast.error('Código já adicionado');
      return;
    }

    // Verifica limite de 10 códigos
    if (garantiaFormData.codigos.length >= 10) {
      toast.error('Limite de códigos atingidos');
      return;
    }

    // Adiciona novo código
    const novoCodigo: CodigoPeca = {
      id: Date.now(), // ID simples baseado no timestamp
      code: codigoCompleto,
      edit: false
    };

    setGarantiaFormData(prev => ({
      ...prev,
      codigos: [novoCodigo, ...prev.codigos] // Adiciona no início como no Angular
    }));
  };

  const removeCodigo = (id: number) => {
    setGarantiaFormData(prev => ({
      ...prev,
      codigos: prev.codigos.filter(c => c.id !== id)
    }));
  };

  const validateGarantiaForm = (): boolean => {
    if (garantiaFormData.nome_cliente && garantiaFormData.nome_cliente.indexOf(' ') === -1) {
      toast.error('Por favor, insira um sobrenome');
      return false;
    }

    if (!garantiaFormData.codigos || garantiaFormData.codigos.length === 0) {
      toast.error('Insira os códigos das peças');
      return false;
    }

    if (garantiaFormData.cpf_cliente.indexOf('.') === -1) {
      const cleanCpf = garantiaFormData.cpf_cliente.replace(/\D/g, '');
      if (cleanCpf.length <= 11 || 
          (cleanCpf.length > 11 && cleanCpf.length < 14) ||
          (cleanCpf.length > 14 && cleanCpf.length < 18)) {
        toast.error('CPF/CNPJ inválido');
        return false;
      }
    }

    return true;
  };

  const submitGarantia = async () => {
    if (!garantiaFormData.nome_cliente || garantiaFormData.codigos.length == 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!validateGarantiaForm()) return;

    setIsSubmittingGarantia(true);
    try {
      const telefoneClean = garantiaFormData.telefone_cliente.replace(/\D/g, '').trim() ;

      const garantiaData = {
        ...garantiaFormData,
        telefone_cliente: telefoneClean,
        iduser: userData?.id,
        sale: saleForGarantia || {}
      };
     

      if(!telefoneClean 
        //|| telefoneClean.length < 11
        ) {
        toast.error('Telefone inválido, verifique e tente novamente');
        setIsSubmittingGarantia(false);
        return;
      }

      if(!garantiaData.cpf_cliente || garantiaData.cpf_cliente.replace(/\D/g, '').length < 11) {
        toast.error('CPF inválido, verifique e tente novamente');
        setIsSubmittingGarantia(false);
        return;
      }


      const response = await saleService.createGarantia(garantiaData);
      
      if (response && response.success !== false) {
        toast.success('Garantia criada com sucesso!');
        closeGarantiaModal();
        // Recarregar dados se necessário
        if (userData) {
          loadData(userData);
        }
      } else {
        toast.error(`Erro ao criar garantia. ${response?.message || ''}`);
        console.error('Erro ao criar garantia:', response);
        let msg = '+++ ERRO AO CRIAR GARANTIA (Minhas Vendas) +++\n';
        msg += `Revendedora: ${userData.codigo} ${userData.nome_razao_social}\n`;
        msg += '_______________\n';
        msg += '> Dados: \n';
        msg += `<pre>${JSON.stringify({
          ...garantiaFormData,
          iduser: userData?.id,
          sale: saleForGarantia || {}
        }, null, 2)}</pre>\n`;            
        msg += '_______________\n';
        msg += `Resposta: <pre>${JSON.stringify(response, null, 2)}</pre>\n`;
        telegramLogService.send(true, msg, new Date());       
        setGarantiaModalOpen(false);
      }
    } catch (error) {
      console.error('Erro ao criar garantia:', error);
      toast.error('Erro ao criar garantia');
    } finally {
      setIsSubmittingGarantia(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold">Aguarde</h2>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Sales Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vendas em aberto do ciclo</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-semibold text-green-600 mb-4">
            {totalSalesMonth?.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })}
          </p>
          <hr className="mt-4 mb-2" />
          <span className="text-[0.65rem] text-muted-foreground/60">
            Total de vendas aguardando "baixa" (situação "em aberto")
          </span>
        </CardContent>
      </Card>

      {/* Filters Card */}
      <Card>
        <CardHeader 
          className="cursor-pointer select-none hover:bg-muted/50 transition-colors rounded-t-lg"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Filtros</CardTitle>
            <div className={cn(
              "transition-transform duration-200",
              filtersExpanded ? "rotate-180" : "rotate-0"
            )}>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </CardHeader>
        <div className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          filtersExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}>
          <CardContent className="space-y-4 pb-6">
          {/* Search Filter */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código, cliente ou método de pagamento..."
              value={searchFilter}
              onChange={(e) => applyFilter(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Date Range Filters */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">Data Início</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">Data Fim</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex gap-2">
              <Button onClick={aplicarFiltroPorPeriodo} disabled={!startDate || !endDate}>
                Aplicar Período
              </Button>
              <Button onClick={limparDatas} variant="outline">
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            </div>
          </div>
          </CardContent>
        </div>
      </Card>

      {/* Sales Table Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Minhas Vendas</CardTitle>
            <div className="flex gap-2">
              {/* <Button onClick={exportPdf} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1" />
                PDF
              </Button> */}
              <Button onClick={exportExcel} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1" />
                Excel
              </Button>
              <Button onClick={() => setInfoModalOpen(true)} variant="outline" size="sm">
                <Info className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <CardDescription>
             Vendas pagas pelos clientes, com status em aberto ou baixado, conforme seu ciclo de revenda.
          </CardDescription>
        </CardHeader>
        <CardContent className='px-2'>
          {filteredSales.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Nenhuma venda encontrada
              </h3>
              <p className="text-sm text-muted-foreground">
                Tente ajustar os filtros para encontrar suas vendas.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredSales.map((sale) => {
                const situationStyles = getSituationStyles(sale.situation);
                return (
                  <Card
                    key={sale.id}
                    className={cn(
                      "transition-all duration-200 hover:shadow-md",
                      situationStyles.card
                    )}
                  >
                    <CardContent className="p-4">
                      {/* Header with Code and Status */}
                      <div className="flex items-start justify-between mb-3">
                        <Badge
                          variant="outline"
                          className={cn("text-xs", situationStyles.badge)}
                        >
                          {getSituationText(sale.situation)}
                        </Badge>
                        <div>
                          <span className="text-lg font-bold text-green-600">
                            {formatCurrency(sale.amount)}
                          </span>
                        </div>
                      </div>
                      <div className='flex items-center gap-1 mb-3'>
                        {/* Client */}
                        <div className="mb-3 w-[65%]">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Cliente:</span>
                          </div>
                          <p className="text-xs">
                            <span className='font-medium'>{sale.nome_cliente || 'Não informado'}<br/></span>
                            {sale.userClient && (<span className='text-muted-foreground'>#{sale.userClient.codigo}</span>)}
                          </p>
                        </div>

                        {/* Payment Method */}
                        <div className="mb-2 text-right w-[45%]">
                          <div className="flex items-center justify-end gap-2 mb-1">
                            {sale.payment_method !== 'pix' && (
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                            )}
                            {sale.payment_method == 'pix' && (
                              <ScanQrCode className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="text-xs text-muted-foreground">Pagamento:</span>
                          </div>
                          <Badge variant="secondary" className="text-[0.70rem] p-[4px]">
                            {mapPaymentMethod(sale.payment_method)} {sale.payment_method !='pix' && `(${sale.installments}x)`}
                          </Badge>
                        </div>
                      </div>


                      {/* Date */}
                      <div className="flex items-center justify-between border-t pt-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[0.65rem] text-muted-foreground">
                              {formatDate(sale.date_created)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[0.65rem] text-muted-foreground">
                              #{sale.codepagarme || `XDINF${sale.id}`}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Sheet>
                            <SheetTrigger asChild>
                              <Button variant="outline" className="p-1">
                                <Settings className="h-4 w-4" /> Ver mais
                              </Button>
                            </SheetTrigger>
                            <SheetContent side="bottom" className="h-auto">
                              <SheetHeader>
                                <SheetTitle>
                                  Venda #{sale.codepagarme || `XDINF${sale.id}`}
                                </SheetTitle>
                              </SheetHeader>
                              <div className="py-4 space-y-2">
                                {sale.warranty_id === 0 ? (
                                  <Button
                                    variant="outline"
                                    className="w-full justify-start border-gray-400 hover:text-black hover:bg-transparent"
                                    onClick={() => {
                                      openGarantiaModal(sale);
                                    }}
                                  >
                                    <Shield className="h-4 w-4 mr-2" />
                                    Gerar Garantia
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    className="w-full justify-start border-gray-400"
                                    onClick={() => {
                                        setSelectedSale(sale);
                                        setWarrantyModalOpen(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Ver Garantia
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  className="w-full justify-start border-gray-400"
                                  onClick={() => {
                                    setSelectedSale(sale);
                                    setReceiptModalOpen(true);
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Ver Recibo
                                </Button>
                              </div>
                            </SheetContent>
                          </Sheet>
                        </div>

                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {filteredSales.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-6 pt-4 border-t gap-2">
              <span className="text-sm text-muted-foreground">
                Total de {filteredSales.length} venda(s) encontrada(s)
              </span>              
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Visualização de Garantia */}
      {selectedSale &&(
        <WarrantyModal
          isOpen={warrantyModalOpen}
          onClose={() => setWarrantyModalOpen(false)}
          selectedSale={selectedSale}
          userData={userData}
          onDownloadWarranty={downloadWarranty}
        />
      )}
     

      {/* Receipt Modal */}
      {selectedSale && (
      <ReceiptModal
        isOpen={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        selectedSale={selectedSale}
        userData={userData}
        onDownloadReceipt={downloadReceipt}
      />
      )}

      {/* Info Modal */}
      <Dialog open={infoModalOpen} onOpenChange={setInfoModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              Saiba Mais
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">💰 Pagamentos</h3>
              <p className="text-sm text-blue-800">
                Todas as vendas exibidas aqui <strong>já foram pagas pelos clientes</strong> e estão em processamento interno pela equipe X do Infinito.
              </p>
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">📊 Status das Venda:</h3>
              
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 mt-0.5">Em Aberto</Badge>
                  <div>
                    <p className="text-sm font-medium">Aguardando baixa</p>
                    <p className="text-xs text-muted-foreground">Venda confirmada, pronta para a "baixa" no sistema e aguardando fechamento do ciclo de vendas.</p>
                  </div>
                </div>
{/*                 
                <div className="flex items-start gap-3">
                  <Badge className="bg-blue-100 text-green-800 border-blue-200 mt-0.5">Aprovado</Badge>
                  <div>
                    <p className="text-sm font-medium">Verificado pela equipe</p>
                    <p className="text-xs text-muted-foreground">Venda aprovada, pronta para "baixa" no sistema</p>
                  </div>
                </div> */}
                
                <div className="flex items-start gap-3">
                  <Badge className="bg-green-100 text-green-800 border-green-200 mt-0.5">Baixado</Badge>
                  <div>
                    <p className="text-sm font-medium">Concluído com sucesso</p>
                    <p className="text-xs text-muted-foreground">Ciclo encerrado e repasses finalizados com sucesso.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 mt-0.5">Reprovado</Badge>
                  <div>
                    <p className="text-sm font-medium">Venda Cancelada</p>
                    <p className="text-xs text-muted-foreground">A venda apresentou inconsistência, estorno, duplicidade ou erro de registro e não foi validada.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Importante:</strong> Os status indicam o processo interno de gerenciamento financeiro da X do Infinito, incluindo conciliações, repasses e validações administrativas.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end pt-4 border-t flex-shrink-0">
            <Button onClick={() => setInfoModalOpen(false)} className="w-full sm:w-auto">
              Entendi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Criação de Garantia */}
      <GarantiaModal
        isOpen={garantiaModalOpen}
        onClose={closeGarantiaModal}
        saleForGarantia={saleForGarantia}
        garantiaFormData={garantiaFormData}
        foundClient={foundClient}
        isSubmittingGarantia={isSubmittingGarantia}
        onCpfChange={handleCpfChange}
        onPhoneChange={handlePhoneChange}
        onNameChange={handleNameChange}
        onSearchClient={searchClient}
        onAddCodigo={addCodigo}
        onRemoveCodigo={removeCodigo}
        onSubmitGarantia={submitGarantia}
      />

      {/* Botão Voltar ao Topo */}
      {showScrollToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 rounded-full h-6 w-6 p-0 shadow-lg bg-primary hover:bg-primary/90 transition-all duration-300"
          size="sm"
        >
          <ChevronUp className="h-2 w-2" />
        </Button>
      )}
    
    </div>
  );
}